import { z } from "zod";
import isPossible from "libphonenumber-js";

// Onboarding Step 1 schemas
export const onboardingStep1EmailSchema = z.object({
  email: z.email({ message: "Email invalide" }),
});
const onboardingStep1PasswordSchema = z
  .object({
    password: z.string().min(8, { message: "Le mot de passe doit contenir au moins 8 caractères" }),
    confirmPassword: z.string().min(1, { message: "Veuillez confirmer le mot de passe" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

// Merge email + password schemas
// Merge email + password schemas while preserving refinements
export const onboardingStep1FullSchema = onboardingStep1EmailSchema.and(onboardingStep1PasswordSchema);

// Inferred form values type
export type OnboardingStep1FormValues = z.infer<typeof onboardingStep1FullSchema>;

export const step2Schema = z.object({
  verificationCode: z.string().length(6, { error: "Le code doit contenir 6 chiffres" }),
});

export const step3Schema = z.object({
  firstName: z.string().min(2, { error: "Prénom requis" }).max(30, { error: "Le prénom ne peut pas dépasser 30 caractères" }),
  lastName: z.string().min(2, { error: "Nom requis" }).max(60, { error: "Le nom ne peut pas dépasser 60 caractères" }),
  phone: z.string().refine(isPossible, { error: "Numéro de téléphone invalide" }),
  clubName: z.string().min(2, { error: "Nom du club requis" }).max(50, { error: "Le nom du club ne peut pas dépasser 50 caractères" }),
  city: z.string().min(2, { error: "Ville requise" }).max(100, { error: "Le nom de la ville ne peut pas dépasser 100 caractères" }),
  postalCode: z.string().min(2, { error: "Code postal requis" }).max(20, { error: "Le code postal ne peut pas dépasser 20 caractères" }),
  country: z.string().min(2, { error: "Pays requis" }).max(100, { error: "Le nom du pays ne peut pas dépasser 100 caractères" }),
  timezone: z.string().min(1, { error: "Fuseau horaire requis" }),
  clubLogo: z
    .file()
    .max(5_000_000, { error: "La taille maximale du logo est de 5 Mo" })
    .mime(["image/png", "image/jpeg", "image/webp"], { error: "Seuls les fichiers PNG, JPEG et WEBP sont autorisés" })
    .optional(),
});

export const step4Schema = z
  .object({
    startDate: z
      .string()
      .min(1, { error: "La date de début est requise" })
      .refine(
        (dateStr) => {
          if (!dateStr || dateStr.length < 10) return false;
          try {
            const [day, month, year] = dateStr.split("/");
            if (!day || !month || !year) return false;
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return date.getFullYear() === parseInt(year) && date.getMonth() === parseInt(month) - 1 && date.getDate() === parseInt(day);
          } catch {
            return false;
          }
        },
        { error: "Format de date invalide (jj/mm/aaaa)" }
      ),
    endDate: z
      .string()
      .min(1, { error: "La date de fin est requise" })
      .refine(
        (dateStr) => {
          if (!dateStr || dateStr.length < 10) return false;
          try {
            const [day, month, year] = dateStr.split("/");
            if (!day || !month || !year) return false;
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return date.getFullYear() === parseInt(year) && date.getMonth() === parseInt(month) - 1 && date.getDate() === parseInt(day);
          } catch {
            return false;
          }
        },
        { error: "Format de date invalide (jj/mm/aaaa)" }
      ),
    openingDays: z.array(z.string()).min(1, { error: "Sélectionnez au moins un jour d'ouverture" }),
    openingHours: z.record(
      z.enum(["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"]),
      z.object({
        matin: z.object({
          active: z.boolean(),
          start: z.string(),
          end: z.string(),
          maxChildren: z.coerce
            .number()
            .int({ message: "Entrer uniquement des nombres pour les enfants" })
            .min(1, "Minimum 1 enfant")
            .max(999, "Maximum 999 enfants")
            .optional(),
        }),
        midi: z.object({
          active: z.boolean(),
          start: z.string(),
          end: z.string(),
          maxChildren: z.coerce
            .number()
            .int({ message: "Entrer uniquement des nombres pour les enfants" })
            .min(1, "Minimum 1 enfant")
            .max(999, "Maximum 999 enfants")
            .optional(),
        }),
        apres: z.object({
          active: z.boolean(),
          start: z.string(),
          end: z.string(),
          maxChildren: z.coerce
            .number()
            .int({ message: "Entrer uniquement des nombres pour les enfants" })
            .min(1, "Minimum 1 enfant")
            .max(999, "Maximum 999 enfants")
            .optional(),
        }),
      })
    ),
  })
  .refine(
    (data) => {
      try {
        const [startDay, startMonth, startYear] = data.startDate.split("/");
        const [endDay, endMonth, endYear] = data.endDate.split("/");

        const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
        const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));

        const diffInMs = endDate.getTime() - startDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays >= 1;
      } catch {
        return false;
      }
    },
    {
      error: "La date de fin doit être au moins 1 jour après la date de début",
      path: ["endDate"],
    }
  )
  .superRefine((data, ctx) => {
    // Validate periods: active => valid start/end and maxChildren, and at least one valid active per selected day
    const toMinutes = (t: string): number | null => {
      if (!t || t === "-" || typeof t !== "string") return null;
      const m = t.match(/^([01]\d|2[0-3])h([0-5]\d)$/);
      if (!m) return null;
      const hh = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      return hh * 60 + mm;
    };

    if (!data.openingDays || !data.openingHours) return;

    for (const day of data.openingDays) {
      const hours = data.openingHours[day as keyof typeof data.openingHours];
      if (!hours) continue;

      const periods = [
        { key: "matin", period: hours.matin },
        { key: "midi", period: hours.midi },
        { key: "apres", period: hours.apres },
      ];

      let hasValidActiveForDay = false;

      for (const { key, period } of periods) {
        if (!period.active) continue;

        const startM = toMinutes(period.start);
        const endM = toMinutes(period.end);

        // Check start time
        if (startM === null) {
          ctx.addIssue({
            code: "custom",
            path: ["openingHours", day, key, "start"],
            message: "Sélectionnez une heure de début",
          });
        }

        // Check end time
        if (endM === null) {
          ctx.addIssue({
            code: "custom",
            path: ["openingHours", day, key, "end"],
            message: "Sélectionnez une heure de fin",
          });
        }

        // Check end > start
        if (startM !== null && endM !== null && endM <= startM) {
          ctx.addIssue({
            code: "custom",
            path: ["openingHours", day, key, "end"],
            message: "L'heure de fin doit être après l'heure de début",
          });
        }

        // Check maxChildren
        if (typeof period.maxChildren !== "number" || period.maxChildren < 1) {
          ctx.addIssue({
            code: "custom",
            path: ["openingHours", day, key, "maxChildren"],
            message: "Saisissez un nombre d'enfants maximum",
          });
        }

        // If all checks pass for this period, mark day as having at least one valid period
        if (startM !== null && endM !== null && endM > startM && typeof period.maxChildren === "number" && period.maxChildren >= 1) {
          hasValidActiveForDay = true;
        }
      }

      // If no valid active period for this day, add error to the day selection
      if (!hasValidActiveForDay) {
        ctx.addIssue({
          code: "custom",
          path: ["openingDays"],
          message: `${day.charAt(0).toUpperCase() + day.slice(1)} : configurez au moins une période complète`,
        });
      }
    }
  });
export const step5Schema = z.object({
  periods: z
    .array(
      z.object({
        type: z.enum(["matin", "midi", "apres-midi"]),
        startTime: z.string(),
        endTime: z.string(),
        maxChildren: z.number().min(1, "Minimum 1 enfant").max(999, "Maximum 999 enfants"),
      })
    )
    .min(1, "Veuillez configurer au moins une période d'accueil"),
});

export const step6Schema = z.object({
  closureDates: z.array(z.string()),
  closurePeriods: z.array(
    z.object({
      date: z.string(),
      periods: z.array(z.enum(["matin", "midi", "apres-midi", "journee"])),
    })
  ),
});

export const step7Schema = z.object({
  name: z.string().min(1, { error: "Le nom du groupe est requis" }).max(30, { error: "Nombre de caractères maximum : 30" }),
  minAge: z.number().min(0, { error: "L'âge minimum doit être positif" }).int({ error: "Entrer uniquement des nombres pour les âges" }),
  maxAge: z.number().min(0, { error: "L'âge maximum doit être positif" }).int({ error: "Entrer uniquement des nombres pour les âges" }),
});

export const step8Schema = z.object({
  subscriptionType: z.enum(["trial", "annual"], {
    error: "Veuillez sélectionner un type d'abonnement",
  }),
});

export type Step2FormValues = z.infer<typeof step2Schema>;
export type Step3FormValues = z.infer<typeof step3Schema>;
export type Step4FormValues = z.infer<typeof step4Schema>;
export type Step5FormValues = z.infer<typeof step5Schema>;
export type Step6FormValues = z.infer<typeof step6Schema>;
export type Step7FormValues = z.infer<typeof step7Schema>;
export type Step8FormValues = z.infer<typeof step8Schema>;
